import {
  MutationFunction,
  QueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
} from '@tanstack/react-query'

import {
  BodyType,
  customInstance,
  ErrorType,
} from '@/features/auth/axios-config'
import { ErrorResponse, RequestFilters } from '@/schema'
import { SortOrder } from '@/types/common'
import { convertSortToApiFormat } from '@/utils'
import { DEFAULT_PAGE, TABLE_PAGE_SIZE } from '@/utils/constants'

import { SecondParameter, userApi } from './services'

const USER_LIST_QUERY_KEY = (params?: (string | number)[]) => [
  'user-list',
  ...(params ? [params] : []),
]

export const useUserList = (params: {
  search?: string
  page?: number
  rowsPerPage?: number
  sort?: { column: string; direction: SortOrder }
  enabled?: boolean
}) => {
  const {
    search = '',
    page = DEFAULT_PAGE,
    rowsPerPage = TABLE_PAGE_SIZE,
    sort,
    enabled = true,
  } = params
  const sortBy = convertSortToApiFormat(
    sort || { column: null, direction: null },
    '+email'
  )

  return useQuery({
    queryKey: USER_LIST_QUERY_KEY([search, page, rowsPerPage, sortBy]),
    queryFn: async () => {
      const { data, pagination } = await userApi.getUserList({
        q: search,
        offset: page * rowsPerPage,
        limit: rowsPerPage,
        sort_by: sortBy,
      })
      return { data, pagination }
    },
    staleTime: 0,
    enabled,
  })
}

/**
 * @summary List all users with optional filtering and sorting
 */
export const usePostListUsers = <
  TError = ErrorType<ErrorResponse>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof userApi.getUserList>>,
      TError,
      { data: BodyType<RequestFilters> },
      TContext
    >
    request?: SecondParameter<typeof customInstance>
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof userApi.getUserList>>,
  TError,
  { data: BodyType<RequestFilters> },
  TContext
> => {
  const mutationKey = ['usePostListUsers']

  const hasCustomMutationKey =
    options?.mutation &&
    'mutationKey' in options.mutation &&
    !!options.mutation.mutationKey

  let resolvedOptions
  if (options === undefined) {
    resolvedOptions = { mutation: { mutationKey }, request: undefined }
  } else if (hasCustomMutationKey) {
    resolvedOptions = options
  } else {
    resolvedOptions = {
      ...options,
      mutation: { ...options.mutation, mutationKey },
    }
  }

  const { mutation: mutationOptions, request: requestOptions } = resolvedOptions

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof userApi.getUserList>>,
    { data: BodyType<RequestFilters> }
  > = (props) => {
    const { data } = props ?? {}

    return userApi.getUserList(data, requestOptions)
  }

  return useMutation({ mutationFn, ...mutationOptions }, queryClient)
}
